import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../App'; 

const CheckoutPage = ({ API }) => {
  const { user, token } = useAuth();
  const [cart, setCart] = useState({ items: [], total: 0 });
  const [productDetails, setProductDetails] = useState({}); // Store product info here
  const [loading, setLoading] = useState(true);
  const [activeStep, setActiveStep] = useState(1); // 1: Bag, 2: Address, 3: Payment
  
  // Donation State
  const [donationAmount, setDonationAmount] = useState(0);
  const [donationEmail, setDonationEmail] = useState('');
  const [showDonationModal, setShowDonationModal] = useState(false);

  // Bag Selection State
  const [selectedItems, setSelectedItems] = useState(new Set());

  // Edit Item State
  const [editingItemIndex, setEditingItemIndex] = useState(null);
  const [tempSize, setTempSize] = useState('');
  const [tempQty, setTempQty] = useState(1);

  // Address State
  const [addressForm, setAddressForm] = useState({
    name: '',
    mobile: '',
    pinCode: '',
    houseNo: '',
    street: '',
    locality: '',
    city: '',
    state: ''
  });

  const navigate = useNavigate();

  // Dummy Offers
  const offers = [
    "10% Instant Discount on BOBCARD Credit Card on a min spend of ₹3,500",
    "5% Unlimited Cashback on Flipkart Axis Bank Credit Card"
  ];

  const availableSizes = ['S', 'M', 'L', 'XL', 'XXL'];
  const availableQuantities = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10];

  useEffect(() => {
    if (user) {
        fetchCart();
        setAddressForm(prev => ({
            ...prev,
            name: user.name || '',
            mobile: user.phone || ''
        }));
    }
  }, [user]);

  const fetchCart = async () => {
    try {
      const config = { headers: { Authorization: `Bearer ${token}` } };
      const response = await axios.get(`${API}/cart`, config);
      setCart(response.data);
      
      // Initialize selected items (all selected by default)
      if (response.data.items) {
        setSelectedItems(new Set(response.data.items.map((_, i) => i)));
      }

      // Fetch details for all products in cart
      if (response.data.items && response.data.items.length > 0) {
        fetchProductDetails(response.data.items);
      } else {
        setLoading(false);
      }
    } catch (error) {
      console.error('Error fetching cart:', error);
      setLoading(false);
    }
  };

  const fetchProductDetails = async (items) => {
    try {
      const details = {};
      await Promise.all(items.map(async (item) => {
        try {
          const res = await axios.get(`${API}/products/${item.product_id}`);
          details[item.product_id] = res.data;
        } catch (err) {
          console.error(`Failed to fetch product ${item.product_id}`, err);
        }
      }));
      setProductDetails(details);
    } catch (error) {
      console.error('Error fetching product details:', error);
    } finally {
      setLoading(false);
    }
  };

  // Selection Handlers
  const toggleItemSelection = (index) => {
    const newSelected = new Set(selectedItems);
    if (newSelected.has(index)) {
        newSelected.delete(index);
    } else {
        newSelected.add(index);
    }
    setSelectedItems(newSelected);
  };

  const toggleAllSelection = () => {
    if (selectedItems.size === cart.items.length) {
        setSelectedItems(new Set()); // Deselect all
    } else {
        setSelectedItems(new Set(cart.items.map((_, i) => i))); // Select all
    }
  };

  // Edit Item Handlers
  const openEditModal = (index, currentSize, currentQty) => {
    setEditingItemIndex(index);
    setTempSize(currentSize);
    setTempQty(currentQty);
  };

  const saveItemChanges = () => {
    if (editingItemIndex !== null) {
        const updatedItems = [...cart.items];
        updatedItems[editingItemIndex] = {
            ...updatedItems[editingItemIndex],
            size: tempSize,
            quantity: tempQty
        };
        setCart({ ...cart, items: updatedItems });
        setEditingItemIndex(null);
        // In a real app, you would also make an API call here to update the cart on the backend
    }
  };

  // Donation Handlers
  const handleDonationClick = (amount) => {
    if (donationAmount === amount) {
      setDonationAmount(0); // Toggle off
    } else {
      setDonationAmount(amount);
    }
  };

  const handleCheckboxChange = (e) => {
    if (e.target.checked) {
      setDonationAmount(10); // Default to 10
    } else {
      setDonationAmount(0);
    }
  };

  // Address Handlers
  const handleAddressChange = (e) => {
    const { name, value } = e.target;
    setAddressForm(prev => ({ ...prev, [name]: value }));
  };

  const handleSaveAddress = () => {
    // Here you would typically save the address to the backend
    console.log("Saving address:", addressForm);
    setActiveStep(3); // Move to Payment
  };

  if (loading) return <div className="p-10 text-center">Loading...</div>;

  // Calculate Totals based on Selection
  const selectedCartItems = cart.items.filter((_, idx) => selectedItems.has(idx));
  
  // Assuming simple logic: MRP = 1.5 * price, Discount = 0.5 * price
  // Using item.quantity to calculate total price
  const totalMRP = selectedCartItems.reduce((acc, item) => acc + (item.price * 1.5 * item.quantity), 0);
  const totalDiscount = selectedCartItems.reduce((acc, item) => acc + (item.price * 0.5 * item.quantity), 0);
  const totalPrice = selectedCartItems.reduce((acc, item) => acc + (item.price * item.quantity), 0);
  
  const finalTotal = totalPrice + donationAmount;

  return (
    <div className="min-h-screen bg-white relative">
      {/* Header */}
      <div className="bg-white border-b sticky top-0 z-50">
        <div className="max-w-screen-xl mx-auto px-4 h-20 flex items-center justify-between">
          <div className="flex items-center gap-8">
            <div className="cursor-pointer" onClick={() => navigate('/')}>
    <img src="/nasiX/fulllogo_transparent_nobuffer.png" alt="nasiX" className="h-12 object-contain" />
      </div>
            <div className="hidden md:flex items-center gap-2 text-xs font-bold tracking-widest text-gray-500">
              <span 
                onClick={() => setActiveStep(1)}
                className={`border-b-2 pb-1 cursor-pointer ${activeStep >= 1 ? 'text-green-600 border-green-600' : 'text-gray-500 border-transparent'}`}
              >
                BAG
              </span>
              <span>----------</span>
              <span 
                onClick={() => activeStep > 1 && setActiveStep(2)}
                className={`border-b-2 pb-1 ${activeStep >= 2 ? 'text-green-600 border-green-600 cursor-pointer' : 'text-gray-500 border-transparent'}`}
              >
                ADDRESS
              </span>
              <span>----------</span>
              <span className={`border-b-2 pb-1 ${activeStep === 3 ? 'text-green-600 border-green-600' : 'text-gray-500 border-transparent'}`}>
                PAYMENT
              </span>
            </div>
          </div>
          <div className="flex items-center gap-2 text-green-600 text-xs font-bold tracking-wide">
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            100% SECURE
          </div>
        </div>
      </div>

      <div className="max-w-screen-xl mx-auto px-4 py-8">
        <div className="flex flex-col lg:flex-row gap-8">
          
          {/* LEFT COLUMN */}
          <div className="flex-1">
            
            {/* STEP 1: BAG VIEW */}
            {activeStep === 1 && (
              <>
                {/* Address Bar */}
                <div className="bg-white border border-gray-200 rounded p-4 mb-4 flex justify-between items-center">
                    <div className="text-sm">
                    Deliver to: <span className="font-bold text-black">{user?.address ? user.address.substring(0, 20) + '...' : 'Select Address'}</span>
                    <span className="text-gray-500 ml-2">{user?.phone ? user.phone : ''}</span>
                    </div>
                    <button 
                    onClick={() => setActiveStep(2)}
                    className="text-pink-600 font-bold text-xs border border-pink-600 px-3 py-2 rounded hover:bg-pink-50 uppercase"
                    >
                    Change Address
                    </button>
                </div>

                {/* Offers Section */}
                <div className="bg-white border border-gray-200 rounded p-4 mb-4">
                    <div className="flex items-center gap-2 mb-3">
                    <svg className="w-5 h-5 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v13m0-13V6a2 2 0 112 2h-2zm0 0V5.5A2.5 2.5 0 109.5 8H12zm-7 4h14M5 12a2 2 0 110-4h14a2 2 0 110 4M5 12v7a2 2 0 002 2h10a2 2 0 002-2v-7" />
                    </svg>
                    <h3 className="font-bold text-gray-700 text-sm uppercase">Available Offers</h3>
                    </div>
                    <ul className="space-y-2">
                    {offers.map((offer, idx) => (
                        <li key={idx} className="text-xs text-gray-600 flex items-start gap-2">
                        <span className="text-gray-400">•</span>
                        {offer}
                        <span className="text-pink-600 font-bold cursor-pointer ml-1">Show More</span>
                        </li>
                    ))}
                    </ul>
                </div>

                {/* Cart Items Header */}
                <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center gap-2">
                        <input 
                            type="checkbox" 
                            checked={selectedItems.size === cart.items.length && cart.items.length > 0} 
                            onChange={toggleAllSelection}
                            className="text-pink-600 focus:ring-pink-500 w-4 h-4" 
                        />
                        <span className="font-bold text-sm text-gray-700">
                            {selectedItems.size}/{cart.items.length} ITEMS SELECTED
                        </span>
                    </div>
                    <div className="flex gap-4 text-xs font-bold text-gray-500">
                        <button className="hover:text-black">REMOVE</button>
                        <span className="text-gray-300">|</span>
                        <button className="hover:text-black">MOVE TO WISHLIST</button>
                    </div>
                </div>

                {/* Cart Items List */}
                <div className="space-y-4">
                    {cart.items.map((item, index) => {
                    const product = productDetails[item.product_id] || {};
                    return (
                        <div key={index} className="bg-white border border-gray-200 rounded p-4 relative flex gap-4">
                            {/* Item Checkbox */}
                            <div className="flex items-start pt-1">
                                <input 
                                    type="checkbox" 
                                    checked={selectedItems.has(index)}
                                    onChange={() => toggleItemSelection(index)}
                                    className="text-pink-600 focus:ring-pink-500 w-4 h-4" 
                                />
                            </div>

                            {/* Image */}
                            <div className="w-24 h-32 bg-gray-100 rounded-sm flex-shrink-0 overflow-hidden">
                                <img 
                                src={product.image_url || "https://via.placeholder.com/150"} 
                                alt={product.name || "Product"} 
                                className="w-full h-full object-cover" 
                                />
                            </div>

                            {/* Details */}
                            <div className="flex-1">
                                <div className="flex justify-between items-start">
                                    <div>
                                        <h3 className="font-bold text-sm text-gray-800">{product.brand || 'House of PM'}</h3>
                                        <p className="text-sm text-gray-600 mb-2">{product.name || 'Loading product details...'}</p>
                                    </div>
                                    <button className="text-gray-400 hover:text-black">
                                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                        </svg>
                                    </button>
                                </div>
                                
                                {/* Size and Qty Selectors */}
                                <div className="flex gap-2 mb-3">
                                    <button 
                                        onClick={() => openEditModal(index, item.size || 'M', item.quantity)}
                                        className="bg-gray-100 rounded px-2 py-1 flex items-center gap-1 cursor-pointer hover:bg-gray-200"
                                    >
                                        <span className="text-xs font-bold text-gray-700">Size: {item.size || 'M'}</span>
                                        <svg className="w-3 h-3 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                                        </svg>
                                    </button>
                                    <button 
                                        onClick={() => openEditModal(index, item.size || 'M', item.quantity)}
                                        className="bg-gray-100 rounded px-2 py-1 flex items-center gap-1 cursor-pointer hover:bg-gray-200"
                                    >
                                        <span className="text-xs font-bold text-gray-700">Qty: {item.quantity}</span>
                                        <svg className="w-3 h-3 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                                        </svg>
                                    </button>
                                </div>

                                <div className="flex items-center gap-2 mb-2">
                                <span className="font-bold text-sm">₹{item.price * item.quantity}</span>
                                <span className="text-xs text-gray-400 line-through">₹{(item.price * 1.5 * item.quantity).toFixed(0)}</span>
                                <span className="text-xs text-orange-500 font-bold">(33% OFF)</span>
                                </div>
                                <p className="text-xs text-gray-500">
                                <span className="font-bold">14 days</span> return available
                                </p>
                            </div>
                        </div>
                    );
                    })}
                </div>
              </>
            )}

            {/* STEP 2: ADDRESS VIEW */}
            {activeStep === 2 && (
                <div className="bg-white p-6 border border-gray-200 rounded">
                    <h3 className="font-bold text-sm mb-4">CONTACT DETAILS</h3>
                    <div className="space-y-4 mb-6">
                        <input 
                            type="text" 
                            name="name"
                            placeholder="Name*" 
                            value={addressForm.name}
                            onChange={handleAddressChange}
                            className="w-full border border-gray-300 rounded p-3 text-sm focus:outline-none focus:border-black"
                        />
                        <input 
                            type="text" 
                            name="mobile"
                            placeholder="Mobile No*" 
                            value={addressForm.mobile}
                            onChange={handleAddressChange}
                            className="w-full border border-gray-300 rounded p-3 text-sm focus:outline-none focus:border-black"
                        />
                    </div>

                    <h3 className="font-bold text-sm mb-4">ADDRESS</h3>
                    <div className="space-y-4">
                        <input 
                            type="text" 
                            name="pinCode"
                            placeholder="Pin Code*" 
                            value={addressForm.pinCode}
                            onChange={handleAddressChange}
                            className="w-full border border-gray-300 rounded p-3 text-sm focus:outline-none focus:border-black"
                        />
                        <input 
                            type="text" 
                            name="houseNo"
                            placeholder="House Number/Tower/Block*" 
                            value={addressForm.houseNo}
                            onChange={handleAddressChange}
                            className="w-full border border-gray-300 rounded p-3 text-sm focus:outline-none focus:border-black"
                        />
                        <input 
                            type="text" 
                            name="street"
                            placeholder="Address (locality, building, street)*" 
                            value={addressForm.street}
                            onChange={handleAddressChange}
                            className="w-full border border-gray-300 rounded p-3 text-sm focus:outline-none focus:border-black"
                        />
                        <input 
                            type="text" 
                            name="locality"
                            placeholder="Locality / Town*" 
                            value={addressForm.locality}
                            onChange={handleAddressChange}
                            className="w-full border border-gray-300 rounded p-3 text-sm focus:outline-none focus:border-black"
                        />
                        <div className="flex gap-4">
                            <input 
                                type="text" 
                                name="city"
                                placeholder="City / District*" 
                                value={addressForm.city}
                                onChange={handleAddressChange}
                                className="w-full border border-gray-300 rounded p-3 text-sm focus:outline-none focus:border-black"
                            />
                            <input 
                                type="text" 
                                name="state"
                                placeholder="State*" 
                                value={addressForm.state}
                                onChange={handleAddressChange}
                                className="w-full border border-gray-300 rounded p-3 text-sm focus:outline-none focus:border-black"
                            />
                        </div>
                    </div>
                    
                    {/* Action Buttons */}
                    <div className="mt-8 pt-4 border-t border-gray-100 flex items-center justify-between">
                        <button 
                            onClick={() => setActiveStep(1)}
                            className="text-sm font-bold text-gray-600 hover:text-black uppercase"
                        >
                            Cancel
                        </button>
                        <button 
                            onClick={handleSaveAddress}
                            className="bg-[#ff3f6c] text-white font-bold py-3 px-12 rounded text-sm tracking-widest hover:bg-[#eb3361] transition-colors uppercase"
                        >
                            Save
                        </button>
                    </div>
                </div>
            )}
          </div>

          {/* RIGHT COLUMN */}
          <div className="w-full lg:w-96">
             
             {/* Coupons - Only visible in Step 1 */}
             {activeStep === 1 && (
               <div className="bg-white border border-gray-200 rounded p-4 mb-4">
                 <h3 className="font-bold text-gray-600 text-xs uppercase mb-3">Coupons</h3>
                 <div className="flex justify-between items-center">
                    <div className="flex items-center gap-3">
                       <svg className="w-5 h-5 text-gray-800" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                         <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z" />
                       </svg>
                       <span className="font-bold text-sm">Apply Coupons</span>
                    </div>
                    <button className="text-pink-600 text-xs font-bold border border-pink-600 px-4 py-1 rounded hover:bg-pink-50">
                       APPLY
                    </button>
                 </div>
               </div>
             )}

             {/* Donation - Only visible in Step 1 */}
             {activeStep === 1 && (
               <div className="bg-white border border-gray-200 rounded p-4 mb-4">
                 <h3 className="font-bold text-gray-600 text-xs uppercase mb-3">Support Transformative Social Work</h3>
                 <div className="flex items-center gap-2 mb-3">
                    <input 
                      type="checkbox" 
                      checked={donationAmount > 0} 
                      onChange={handleCheckboxChange}
                      className="text-pink-600 focus:ring-pink-500 rounded cursor-pointer" 
                    />
                    <span className="text-sm font-bold text-gray-700">Donate and make a difference</span>
                 </div>
                 <div className="flex gap-2 mb-3">
                    {[10, 20, 50, 100].map(amt => (
                      <button 
                        key={amt} 
                        onClick={() => handleDonationClick(amt)}
                        className={`border rounded-full px-4 py-1 text-sm font-bold transition-colors ${
                          donationAmount === amt 
                            ? 'border-pink-600 text-pink-600 bg-pink-50' 
                            : 'border-gray-300 text-gray-700 hover:border-black'
                        }`}
                      >
                        ₹{amt}
                      </button>
                    ))}
                 </div>
                 
                 {donationAmount > 0 && (
                   <div className="mb-3 animate-fade-in">
                     <p className="text-xs text-gray-600 mb-2">We will send donation receipt on the provided email id.</p>
                     <input 
                       type="email" 
                       value={donationEmail}
                       onChange={(e) => setDonationEmail(e.target.value)}
                       placeholder="Enter your email"
                       className="w-full border border-gray-300 rounded px-3 py-2 text-sm focus:outline-none focus:border-pink-600"
                     />
                   </div>
                 )}

                 <button 
                   onClick={() => setShowDonationModal(true)}
                   className="text-pink-600 text-xs font-bold hover:underline"
                 >
                   Know More
                 </button>
               </div>
             )}

             {/* Price Details */}
             <div className="bg-white p-4 border border-gray-200 rounded-sm">
              <h3 className="font-bold text-gray-600 text-xs uppercase mb-4">Price Details ({selectedItems.size} Items)</h3>
              
              <div className="space-y-3 text-sm mb-4 border-b border-gray-200 pb-4">
                <div className="flex justify-between">
                  <span className="text-gray-600">Total MRP</span>
                  <span>₹{totalMRP.toFixed(2)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Discount on MRP</span>
                  <span className="text-green-500">-₹{totalDiscount.toFixed(2)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Coupon Discount</span>
                  <span className="text-pink-600 cursor-pointer">Apply Coupon</span>
                </div>
                
                {donationAmount > 0 && (
                  <div className="flex justify-between">
                    <span className="text-gray-600">Social Work Donation</span>
                    <span>₹{donationAmount}</span>
                  </div>
                )}

                <div className="flex justify-between">
                  <span className="text-gray-600">Platform Fee</span>
                  <span className="text-green-500">FREE</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Shipping Fee</span>
                  <span className="text-green-500">FREE</span>
                </div>
              </div>

              <div className="flex justify-between font-bold text-lg mb-6">
                <span>Total Amount</span>
                <span>₹{finalTotal.toFixed(2)}</span>
              </div>

              {activeStep === 1 ? (
                <button
                  onClick={() => setActiveStep(2)}
                  className="w-full bg-[#ff3f6c] text-white font-bold py-4 rounded text-sm tracking-widest hover:bg-[#eb3361] transition-colors uppercase"
                >
                  PLACE ORDER
                </button>
              ) : activeStep === 2 ? (
                 // No button here in right column for Step 2, as it's in the form
                 null
              ) : (
                <button
                  // onClick={initiatePayment} // Uncomment when payment logic is ready
                  className="w-full bg-[#ff3f6c] text-white font-bold py-4 rounded text-sm tracking-widest hover:bg-[#eb3361] transition-colors uppercase"
                >
                  PAY NOW
                </button>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Donation Modal */}
      {showDonationModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-[60] flex items-center justify-center p-4">
          <div className="bg-white rounded-lg w-full max-w-md p-6 relative animate-fade-in-up">
            <button 
              onClick={() => setShowDonationModal(false)}
              className="absolute top-4 right-4 text-gray-500 hover:text-black"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
            
            <h3 className="font-bold text-lg mb-6">Donate and make a difference</h3>
            
            <div className="flex items-center gap-4 mb-6">
               {/* Placeholders for logos since we don't have the exact assets */}
               <div className="h-8 flex items-center">
                 <span className="font-bold text-orange-500 text-xl italic">nasiX Foundation</span>
               </div>
            </div>

            <ul className="space-y-4 text-sm text-gray-600 list-disc pl-5 mb-6">
              <li>Donation amount is being collected on behalf of Flipkart Foundation</li>
              <li>Donation amount is non-refundable.</li>
              <li>Myntra will share your Name, phone number, Email ID and Address with Flipkart Foundation.</li>
              <li>No 80G receipt will be provided for this donation.</li>
            </ul>

            <div className="text-xs text-pink-600 font-bold cursor-pointer hover:underline">
              Have a question? check FAQs
            </div>
          </div>
        </div>
      )}

      {/* Size/Qty Edit Modal */}
      {editingItemIndex !== null && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-[60] flex items-center justify-center p-4">
            <div className="bg-white rounded-lg w-full max-w-sm p-6 relative animate-fade-in-up">
                <button 
                    onClick={() => setEditingItemIndex(null)}
                    className="absolute top-4 right-4 text-gray-500 hover:text-black"
                >
                    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                </button>

                <h3 className="font-bold text-lg mb-6">Select Size & Quantity</h3>

                <div className="mb-6">
                    <h4 className="font-bold text-sm text-gray-700 mb-3">Select Size</h4>
                    <div className="flex flex-wrap gap-3">
                        {availableSizes.map(size => (
                            <button
                                key={size}
                                onClick={() => setTempSize(size)}
                                className={`w-10 h-10 rounded-full border flex items-center justify-center text-sm font-bold transition-colors ${
                                    tempSize === size 
                                        ? 'border-pink-600 text-pink-600 bg-pink-50' 
                                        : 'border-gray-300 text-gray-700 hover:border-black'
                                }`}
                            >
                                {size}
                            </button>
                        ))}
                    </div>
                </div>

                <div className="mb-8">
                    <h4 className="font-bold text-sm text-gray-700 mb-3">Select Quantity</h4>
                    <div className="flex flex-wrap gap-3">
                        {availableQuantities.map(qty => (
                            <button
                                key={qty}
                                onClick={() => setTempQty(qty)}
                                className={`w-10 h-10 rounded-full border flex items-center justify-center text-sm font-bold transition-colors ${
                                    tempQty === qty 
                                        ? 'border-pink-600 text-pink-600 bg-pink-50' 
                                        : 'border-gray-300 text-gray-700 hover:border-black'
                                }`}
                            >
                                {qty}
                            </button>
                        ))}
                    </div>
                </div>

                <button
                    onClick={saveItemChanges}
                    className="w-full bg-[#ff3f6c] text-white font-bold py-3 rounded text-sm tracking-widest hover:bg-[#eb3361] transition-colors uppercase"
                >
                    DONE
                </button>
            </div>
        </div>
      )}
    </div>
  );
};

export default CheckoutPage;